import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const GallerySettingsPanel = ({ 
  galleryLink, 
  expirationDate,
  isPublic,
  requiresAuth,
  showMediaInfo = true,
  onUpdateSettings,
  onCopyLink,
  onRegenerateLink 
}) => {
  const [linkCopied, setLinkCopied] = useState(false);
  const [settings, setSettings] = useState({
    isPublic,
    requiresAuth,
    expirationDate,
    showMediaInfo
  });

  const handleCopyLink = () => {
    navigator.clipboard?.writeText(galleryLink);
    setLinkCopied(true);
    onCopyLink();
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const handleSettingChange = (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    onUpdateSettings(newSettings);
  };

  const formatExpirationDate = (date) => {
    if (!date) return 'Never';
    return new Date(date)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="bg-card rounded-xl border border-border p-4 md:p-6">
      <div className="flex items-start justify-between mb-4 md:mb-6">
        <div>
          <h3 className="text-base md:text-lg font-semibold mb-1">
            Gallery Settings
          </h3>
          <p className="text-xs md:text-sm text-muted-foreground">
            Access control and sharing options
          </p>
        </div>
        
        <div className="p-2 rounded-lg bg-primary/10">
          <Icon name="Settings" size={20} className="text-primary" />
        </div>
      </div>
      <div className="space-y-4 md:space-y-6">
        <div className="p-3 md:p-4 bg-muted rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="Link" size={16} className="text-primary" />
            <span className="text-xs md:text-sm font-medium">Shareable Link</span>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              type="text"
              value={galleryLink}
              readOnly
              className="flex-1 text-xs md:text-sm"
            />
            <div className="flex gap-2">
              <Button
                variant={linkCopied ? "success" : "outline"}
                size="sm"
                iconName={linkCopied ? "Check" : "Copy"}
                onClick={handleCopyLink}
                className="flex-1 sm:flex-none"
              >
                {linkCopied ? 'Copied!' : 'Copy'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="RefreshCw"
                onClick={onRegenerateLink}
                className="flex-1 sm:flex-none"
              >
                <span className="hidden md:inline">Regenerate</span>
              </Button>
            </div>
          </div>
        </div>

        <div className="space-y-3 md:space-y-4">
          <Checkbox
            label="Public Gallery"
            description="Anyone with the link can view this gallery"
            checked={settings?.isPublic}
            onChange={(e) => handleSettingChange('isPublic', e?.target?.checked)}
          />

          <Checkbox
            label="Require Visitor Authentication"
            description="Visitors must provide name, email, and phone to access"
            checked={settings?.requiresAuth}
            onChange={(e) => handleSettingChange('requiresAuth', e?.target?.checked)}
          />

          <Checkbox
            label="Show Media Titles & Descriptions"
            description="Display media titles and descriptions to visitors"
            checked={settings?.showMediaInfo}
            onChange={(e) => handleSettingChange('showMediaInfo', e?.target?.checked)}
          />
        </div>

        <div className="p-3 md:p-4 bg-muted rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Icon name="Calendar" size={16} className="text-muted-foreground" />
              <span className="text-xs md:text-sm font-medium">Link Expiration</span>
            </div>
            <span className="text-xs md:text-sm text-muted-foreground">
              {formatExpirationDate(settings?.expirationDate)}
            </span>
          </div>
          
          <Input
            type="date"
            value={settings?.expirationDate || ''}
            onChange={(e) => handleSettingChange('expirationDate', e?.target?.value)}
            className="w-full"
          />
        </div>

        <div className="grid grid-cols-2 gap-3 md:gap-4 pt-2">
          <div className="p-3 md:p-4 bg-success/10 rounded-lg text-center">
            <Icon name="Eye" size={20} className="text-success mx-auto mb-2" />
            <p className="text-xs text-muted-foreground mb-1">Total Views</p>
            <p className="text-lg md:text-xl font-semibold data-text">1,247</p>
          </div>
          
          <div className="p-3 md:p-4 bg-accent/10 rounded-lg text-center">
            <Icon name="Users" size={20} className="text-accent mx-auto mb-2" />
            <p className="text-xs text-muted-foreground mb-1">Unique Visitors</p>
            <p className="text-lg md:text-xl font-semibold data-text">89</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GallerySettingsPanel;